import fetchRetry from 'fetch-retry';
import { isObject, isString, pick } from 'lodash';
import moment from 'moment-timezone';
import qs from 'qs';
import Url from 'url-parse';
import { v4 as uuidv4 } from 'uuid';
import {
  type FunnelTrackEventEnum,
  type RestExperience,
  type RestModalCard,
  type RestModalForwards,
  type RestMultiRowSurveyCard,
  type TopicEnum,
} from '../contracts';
import { RestModalAction, type SyncConfig } from './action-contracts';

type ISOString = string;
type GenericURL = string;
type NavigatorUserAgent = string;
type GeneratedEventMetadata = {
  guid: string;
  context: {
    locale: string;
    timezone: string;
    user_agent: NavigatorUserAgent;
    url: GenericURL;
    referrer: GenericURL;
  };
  timestamp: ISOString;
  /**
   * Optional because it always could be. This was declared `string` while
   * being populated from a lodash `get` on a path segment, which returns `any`
   * and so hid the absent case: a path with fewer than three segments yields
   * no key and `JSON.stringify` drops the field. A type correction, not a
   * behavior change.
   */
  session_key: string | undefined;
};

export enum EventSource {
  LIFECYCLE_OFFER = 'LIFECYCLE_OFFER',
  DEFLECTION = 'DEFLECTION',
  PRICING_PAGE = 'PRICING_PAGE',
}

export type PricingPageEventProperties = {
  visitor_id: string;
  billing_system_id: string;
  quantity: number;
  addons_with_quantity: Record<string, number>;
  billing_system_ids_displayed: string[];
  checkout_data: Record<string, any>;
  pricing_page_id: string;
  pricing_page_ev_id: number;
};

export type EventObject = {
  source?: EventSource;
  session: string;
  cta_id?: string;
  id: string | 'no_id' | null /* for loss aversions */;
  url?: string;
  name?: string;
  version: string;
  app_id: string | null;
  user_id?: string | null;
  event:
    | TopicEnum
    | TopicEnum[keyof TopicEnum]
    | FunnelTrackEventEnum
    | FunnelTrackEventEnum[keyof FunnelTrackEventEnum];
  card_version?: string;
  action?: RestModalAction | RestModalAction[keyof RestModalAction];
  properties: {
    type?: string;
    name?: keyof RestMultiRowSurveyCard['provides'];
    option?: string | boolean;
    sync?: SyncConfig;
    modal?: RestModalCard;
    message?: string;
    survey_state?: RestMultiRowSurveyCard['provides'];
  };
  component?: {
    id: string;
    name: string;
  };
  direction?: EventDirectionEnum | EventDirectionEnum[keyof EventDirectionEnum];
  type?: string;
  validation?: RestModalForwards['validation'];
  form?: SyncConfig;
  data?: {
    presentation: string;
    template: string;
    position?: string;
  };
  pricing_page_properties?: Partial<PricingPageEventProperties>;
  guid?: string;
};
type ReportedEventType = EventObject & GeneratedEventMetadata;

enum EventDirectionEnum {
  forwards = 'forwards',
  backwards = 'backwards',
}
export type EventSyncObject = Pick<RestModalForwards, 'type'> & {
  session: string;
  id?: string | null;
  name: string;
  version: string;
  app_id: string | null;
  action?: RestModalAction | RestModalAction[keyof RestModalAction];
  properties?: {
    sync?: SyncConfig;
    modal?: RestModalCard;
    message?: string;
    survey_state?: RestMultiRowSurveyCard['provides'];
    id?: string;
    name?: string;
  };
  component?: {
    id: string;
    name: string;
  };
  direction?: EventDirectionEnum | EventDirectionEnum[keyof EventDirectionEnum];
  survey_state?: RestMultiRowSurveyCard['provides'];
  validation?: RestModalForwards['validation'];
  card_version?: string;
  url?: string | null;
  lco_redirect_url?: string | null;
  modal?: string;
  form: SyncConfig & {
    sync?: SyncConfig;
    modal?: RestModalCard;
    message?: string;
    includeSurveyAnswers?: boolean;
    appendAnswersToHash?: boolean;
  };
  isCheckoutProcessing?: boolean;
};
type ReportedSyncObject = EventSyncObject & GeneratedEventMetadata;

export type SubmissionConfig = {
  appendAnswersToHash?: boolean;
  includeSurveyAnswers?: boolean;
};

/**
 * Performs the top-level navigation a `link` sync asks for. Hosted pages own
 * their own window and navigate directly; embedded runs hand the URL to the
 * merchant page instead, because a cross-origin iframe cannot navigate its
 * parent. The action and the URL are identical either way, so this is the only
 * thing the two modes disagree about.
 */
export type FunnelNavigate = (url: string) => void;

export const navigateInThisWindow: FunnelNavigate = (url) => {
  window.location.href = url;
};

const fetchWithRetry = fetchRetry(fetch, {
  retryDelay: (attempt: number) => Math.pow(2, attempt) * 100, // 100, 200, 400, 800, etc
});

const LOG_RETRY_ATTEMPTS = 8;

/**
 * A value, or a function producing one per use.
 *
 * The distinction is load-bearing rather than a convenience. The hosted funnel
 * instantiates `Events` at module scope, which runs before the loader has
 * fetched the experience and populated config and the authenticity token, and
 * the token is then replaced every 24 hours because it expires after two days.
 * Resolving eagerly would capture `undefined` and never recover. A merchant
 * embedding the SDK can pass plain values.
 */
export type Resolvable<T> = T | (() => T);

const resolve = <T>(value: Resolvable<T>): T =>
  typeof value === 'function' ? (value as () => T)() : value;

/**
 * Endpoints are individually optional because today they come from
 * `bbkClientConfig?.event_api_endpoint` and friends, which are absent until the
 * experience has loaded.
 */
export type EventTransportEndpoints = {
  event?: string;
  sync?: string;
  eventLogs?: string;
  syncLogs?: string;
};

export type SessionKeyResolver = (
  session: RestExperience['session']
) => string | undefined;

export type EventTransportConfig = {
  endpoints: Resolvable<EventTransportEndpoints>;
  /** Sent as `BBK-Authenticity-Token`. A bearer credential, not a CSRF pair. */
  authenticityToken: Resolvable<string>;
  /**
   * Reported as `session_key` in every event and sync. A merchant passes a
   * plain string. The resolver form receives the session being reported
   * because the hosted funnel needs it: on a lifecycle path `session_key` is
   * taken from the payload rather than the URL, so it varies per event.
   */
  sessionKey: string | undefined | SessionKeyResolver;
  /** When true, nothing is delivered and no link redirect is followed. */
  dryRun: Resolvable<boolean>;
  debug?: Resolvable<boolean>;
  fetch?: typeof fetchWithRetry;
  /** Host diagnostics; defaults to the browser console. */
  diagnostics?: Pick<Console, 'warn' | 'error'>;
};

export interface NextStepHandler {
  (modalKey?: string, extra?: object | string): void;
}

export interface ExecuteEventsHandler {
  ({
    events,
    syncs,
    synchronous,
  }: {
    events?: EventObject[];
    syncs?: EventSyncObject[];
    synchronous?: boolean;
  }): void;
}

export class Events {
  private debug = false;

  private readonly config: EventTransportConfig;

  private readonly fetch: typeof fetchWithRetry;

  constructor(config: EventTransportConfig) {
    this.config = config;
    this.fetch = config.fetch ?? fetchWithRetry;
  }

  private logger(...args: unknown[]): void {
    if (resolve(this.config.debug ?? false) || this.debug) {
      console.log(...args);
      if (args.length > 0) console.log('~~~~~~~~~~~');
    }
  }

  private authenticityToken(): string {
    return resolve(this.config.authenticityToken);
  }

  private returnUrls() {
    const endpoints = resolve(this.config.endpoints);

    return {
      eventUrl: endpoints.event,
      syncUrl: endpoints.sync,
      // Additional copies of events and syncs are sent to a logging endpoint
      // as a double backup safety
      eventLogUrl: endpoints.eventLogs,
      eventSyncLogUrl: endpoints.syncLogs,
    };
  }

  private checkDryRun(data?: EventObject | EventSyncObject | string) {
    const isDryRun = resolve(this.config.dryRun);
    if (isDryRun) {
      this.logger('[SYNC] IS DRY RUN. No sync events will be triggered.');
      this.logger(JSON.stringify(data, null, 2));
    }
    return isDryRun;
  }

  private generateMetadata(session: RestExperience['session']) {
    return {
      guid: `${session}-${uuidv4()}`,
      context: {
        locale: navigator.language,
        timezone: moment.tz.guess(),
        user_agent: navigator.userAgent,
        url: window.location.href,
        referrer: document.referrer,
      },
      timestamp: moment().toISOString(),
      session_key:
        typeof this.config.sessionKey === 'function'
          ? this.config.sessionKey(session)
          : this.config.sessionKey,
    };
  }

  private decorateUrlWithAnswers({
    url,
    eventSync,
    config,
  }: {
    url: string;
    eventSync: EventSyncObject;
    config: SubmissionConfig;
  }) {
    let redirectUrl = url;
    if (eventSync.form.includeSurveyAnswers || config.includeSurveyAnswers) {
      try {
        const parsedUrl = Url(url);
        const existingQuery = qs.parse(parsedUrl.query, {
          ignoreQueryPrefix: true,
        });
        const answers = eventSync.survey_state!;
        const reasons = answers.reason_for_leaving.map((rl) => rl.value);
        const feedback = answers.feedback.trim().substring(0, 140);
        const reasonOther = answers.reason_other?.trim().substring(0, 140);
        const newQuery = qs.stringify({
          ...existingQuery,
          ...pick(answers, ['competition', 'sentiment']),
          reason: reasons.join(),
          reason_other: reasonOther?.length ? reasonOther : undefined,
          feedback,
        });
        if (eventSync.form.appendAnswersToHash || config.appendAnswersToHash) {
          let newHash = `${parsedUrl.hash}${newQuery}`;
          if (parsedUrl.hash) {
            newHash = `${parsedUrl.hash}&${newQuery}`;
          }
          parsedUrl.set('hash', newHash);
        } else {
          parsedUrl.set('query', newQuery);
        }
        redirectUrl = parsedUrl.toString();
      } catch (e) {
        console.error('e', e);
      }
    }
    return redirectUrl;
  }

  processLinkRedirection(
    result: { error?: unknown },
    currentEventSync: EventSyncObject,
    config: SubmissionConfig,
    navigate: FunnelNavigate = navigateInThisWindow
  ) {
    if (result.error) {
      // already reported this error, cannot do anything
      return;
    }
    if (
      currentEventSync.action === RestModalAction.link &&
      isString(currentEventSync.url)
    ) {
      const url = this.decorateUrlWithAnswers({
        url: currentEventSync.url,
        eventSync: currentEventSync,
        config,
      });
      console.log(
        `CBRetention: Redirecting to ${url} at ${new Date().toISOString()}`
      );
      navigate(url);
    }
  }

  async executeEventQueue({
    events = [],
    syncs = [],
    synchronous = false,
    config = {},
    navigate = navigateInThisWindow,
  }: {
    events?: EventObject[];
    syncs?: EventSyncObject[];
    synchronous?: boolean;
    config?: SubmissionConfig;
    navigate?: FunnelNavigate;
  }) {
    this.logger(
      '[executeEventQueue] synchronous?',
      synchronous,
      'events:',
      events,
      'event syncs:',
      syncs
    );
    if (this.checkDryRun()) {
      return;
    }

    if (synchronous) {
      const resolveEvents = await Promise.all(
        events.map((event) => this.trackEvent(event))
      );

      this.logger(
        '[executeEventQueue] SYNCHRONOUS EVENTS RESOLVED',
        resolveEvents
      );

      const resolvedSyncs = await Promise.all(
        syncs.map((s) => this.trackSync(s))
      );

      this.logger(
        '[executeEventQueue] SYNCHRONOUS EVENT SYNCS RESOLVED',
        resolvedSyncs
      );

      resolvedSyncs.map((rs, i) =>
        this.processLinkRedirection(rs, syncs[i]!, config, navigate)
      );
    } else {
      Promise.all(events.map((event) => this.trackEvent(event)));
      Promise.all(syncs.map((s) => this.trackSync(s))).then((res) =>
        res.map((rs, i) =>
          this.processLinkRedirection(rs, syncs[i]!, config, navigate)
        )
      );
    }
  }

  private trackEvent(event: EventObject) {
    const sessionId =
      event.source === EventSource.PRICING_PAGE
        ? (event.pricing_page_properties?.visitor_id ?? '')
        : event.session;
    const reportableEvent: ReportedEventType = {
      ...event,
      ...this.generateMetadata(sessionId),
    };

    // should not block on anything
    this.backupLogEvent(reportableEvent);

    // needs to return a promise
    return this.logEvent(reportableEvent);
  }

  private async logEvent(event: ReportedEventType) {
    const { eventUrl } = this.returnUrls();
    const logRes = await this.fetch(`${eventUrl}?event=${event.event}`, {
      body: JSON.stringify(event),
      method: 'POST',
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'BBK-Authenticity-Token': this.authenticityToken(),
      },
      keepalive: true,
      retryOn: (attempt, error, response) => {
        // retry on any network error, or 4xx or 5xx status codes
        if (error !== null || (response && response.status >= 400)) {
          this.logger(`Retrying event send, attempt number ${attempt + 1}`);
          return true;
        }
        return false;
      },
    })
      .then((resp) => (resp.ok ? resp.json() : event))
      .catch((e) => console.error(e));

    this.logger('[logEvent] Event fully logged ', event.event, logRes);

    // success
    if (isObject(logRes) || logRes === null) {
      return logRes;
    }

    return {
      error: true,
      event: logRes,
    };
  }

  private backupLogEvent(event: ReportedEventType) {
    const { eventLogUrl } = this.returnUrls();
    return this.fetch(`${eventLogUrl}?backup_event_log`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'BBK-Authenticity-Token': this.authenticityToken(),
      },
      body: JSON.stringify(event),
      retryOn: (attempt, error, response) => {
        // retry on any network error, or 4xx or 5xx status codes
        if (error !== null || (response && response?.status >= 400)) {
          this.logger(
            `Retrying backup event logging, attempt number ${attempt + 1}`
          );
          return true;
        }

        return false;
      },
    })
      .then((r) => r.json())
      .catch((e) => e);
  }

  private trackSync(eventSync: EventSyncObject) {
    const reportableSync: ReportedSyncObject = {
      ...eventSync,
      ...this.generateMetadata(eventSync.session),
    };

    // should not block on anything
    this.backupLogSync(reportableSync);

    // needs to return a promise
    return this.logSync(reportableSync);
  }

  private async logSync(eventSync: ReportedSyncObject) {
    const { syncUrl } = this.returnUrls();
    const logRes = await this.fetch(`${syncUrl}?${eventSync.action}`, {
      body: JSON.stringify(eventSync),
      method: 'POST',
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'BBK-Authenticity-Token': this.authenticityToken(),
      },
      keepalive: true,
      retryOn: (attempt, error, response) => {
        // retry on any network error, or 4xx or 5xx status codes
        if (error !== null || (response && response.status >= 400)) {
          this.logger(
            `Retrying event sync send, attempt number ${attempt + 1}`
          );
          return true;
        }
        return false;
      },
    })
      .then((resp) => (resp.ok ? resp.json() : false))
      .catch((e) => console.error(e));

    this.logger('[logSync] Event sync fully logged ', eventSync.action, logRes);

    // success
    if (isObject(logRes) || logRes === null) {
      return { error: false, eventSync: logRes };
    }

    return {
      error: true,
      eventSync: eventSync,
    };
  }

  private backupLogSync(eventSync: ReportedSyncObject) {
    const { eventSyncLogUrl } = this.returnUrls();
    return this.fetch(`${eventSyncLogUrl}?backup_event_sync_log`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'BBK-Authenticity-Token': this.authenticityToken(),
      },
      body: JSON.stringify(eventSync),
      retryOn: (attempt, error, response) => {
        if (attempt >= LOG_RETRY_ATTEMPTS) {
          const msg = `Backup event sync logging failed for: ${JSON.stringify(eventSync)}`;
          this.logger(msg);
          (this.config.diagnostics ?? console).warn(msg);
          if (error) {
            (this.config.diagnostics ?? console).error(error);
          }
          return false;
        }

        // retry on any network error, or 4xx or 5xx status codes
        if (error !== null || (response && response?.status >= 400)) {
          this.logger(
            `Retrying backup event sync logging, attempt number ${attempt + 1}`
          );
          return true;
        }

        return false;
      },
    })
      .then((r) => r.json())
      .catch((e) => e);
  }
}
